import * as Sentry from "@sentry/react";
import Clarity from "@microsoft/clarity";
import { registerSW } from "virtual:pwa-register";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { ErrorBoundary } from "react-error-boundary";
import { browserTracingIntegration } from "@sentry/react";
import { providerFromUrl } from "./utils/payments/provider";
import "./index.css";
import ErrorFallback from "./ui/ErrorFallback";
import App from "./App.js";
import store from "./store/index.js";

//For payment method
providerFromUrl();

// Initialize Sentry only if the environment variable indicates it's enabled
if (
  import.meta.env.VITE_ENABLE_SENTRY === "true" &&
  import.meta.env.VITE_SENTRY_DSN
) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [browserTracingIntegration()],
    tracesSampleRate: 1.0,
    release: import.meta.env.VITE_APP_VERSION,
    environment: import.meta.env.MODE,
  });
}

// Initialize Clarity only if the environment variable indicates it's enabled
if (import.meta.env.VITE_CLARITY_ID) {
  Clarity.init(import.meta.env.VITE_CLARITY_ID);
}

// Register service worker and auto-refresh when an update is available
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    // Automatically activate the new SW and reload
    updateSW(true);
  },
  onOfflineReady() {},
});

const container = document.getElementById("root");

if (!container) {
  throw new Error(
    "Root element not found. Make sure there's an element with id='root' in your index.html."
  );
}

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => window.location.replace("/")}
      onError={(error, info) => {
        Sentry.captureException(error, {
          extra: {
            componentStack: info.componentStack,
          },
        });
      }}
    >
      <Provider store={store}>
        <App />
      </Provider>
    </ErrorBoundary>
  </StrictMode>
);
